//react
import React, { useMemo } from "react";

//third-party
import { useForm } from "react-hook-form";
import { Button, Form } from "react-bootstrap";
import { Calendar, Sunrise } from "react-bootstrap-icons";
import { RiPlayReverseFill, RiPlayFill, RiPauseFill } from "react-icons/ri";
import { DateTimeFormatter, Instant, ZoneOffset } from "@js-joda/core";

//satellite
import { geocodeLocation } from "../api/Mapbox";

export interface ToolbarProps {
    now:          Date;
    mode:         SimMode;
    rate:         number;
    sunPosition:  number | undefined;
    chooseDate: () => void;
    rewind:     () => void;
    play:       () => void;
    pause:      () => void;    
    turbo:      () => void;
    onLocationFound?: ( lat: number, lng: number, name: string ) => void;
}

interface ToolbarForm {
    target: string;
}

export enum SimMode {
    Rewind,
    Play,
    PlayPaused,
    Pause,
    RewindPaused
}

export function Toolbar( { now, mode, chooseDate, rewind, play, pause, turbo, 
                           rate, sunPosition: sunPos, onLocationFound }: ToolbarProps ) {
    const { handleSubmit, register } = useForm<ToolbarForm>( { defaultValues: {} } );
    async function onSubmit( data: ToolbarForm ) {
        if( !data.target?.trim() ) {
            return;
        }

        try {
            const result = await geocodeLocation( data.target );
            if( result ) {
                console.log( `Found: ${data.target} at ${result.lat}, ${result.lng}` );
                onLocationFound?.( result.lat, result.lng, data.target );
            } else {
                console.warn( `Location not found: ${data.target}` );
            }
        } catch( error ) {
            console.error( "Geocoding error:", error );
        }
    }

    const rewindCls      = mode  === SimMode.Rewind ? "bg-primary" : "text-body bg-body-secondary";
    const playCls        = mode  === SimMode.Play   ? "bg-primary" : "text-body bg-body-secondary";
    const pauseCls       = mode  === SimMode.Pause  ? "bg-primary" : "text-body bg-body-secondary";
    const size           = 24;
    const commonBtnStyle = "d-flex align-items-center py-1 p-2 border-2";    
    const nowZdt         = Instant.ofEpochMilli( now.getTime() ).atZone( ZoneOffset.UTC );
    const formatter      = useMemo( () => DateTimeFormatter.ofPattern("MM/dd/yyyy HH:mm:ss" ), [] );

    return <div className="d-flex flex-column gap-2">
        <div className="w-100">
            <Form onSubmit={handleSubmit( onSubmit )}>
                <Form.Control
                    className="w-100"
                    type="text"
                    {...register( "target", { required: true } )}
                    placeholder="Enter City Name"
                />
            </Form>
        </div>
        <div className="d-flex flex-row align-items-center gap-1">
            <Button className={`${commonBtnStyle} text-body bg-body-secondary`} onClick={() => chooseDate()} >
                <Calendar size={size} />
            </Button>
            <Button className={`${rewindCls} ${commonBtnStyle}`} onClick={() => rewind()} >
                <RiPlayReverseFill size={size} />
            </Button>
            <Button className={`${playCls} ${commonBtnStyle}`} onClick={() => play()}>
                <RiPlayFill size={size} />
            </Button>
            <Button className={`${pauseCls} ${commonBtnStyle}`} onClick={() => pause()} >
                <RiPauseFill size={size} />
            </Button>
            <Button className={`font-monospace ${commonBtnStyle}`} onClick={() => turbo()} >
                {`${String( rate ).padStart(4, '0')}x`}
            </Button>
        </div>
        <div className="w-100 bg-body shadow border border-1 p-2 rounded">
            Time: {nowZdt.format( formatter )} <Sunrise /> {sunPos?.toFixed(0)} 
        </div>
    </div>;
}